import React from 'react';
import { SafetyAction, SafetyActionDashboardStats } from '../types';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface OverdueAgeingViewProps {
  actions: SafetyAction[];
  stats: SafetyActionDashboardStats | null;
  onSelect: (action: SafetyAction) => void;
}

export const OverdueAgeingView: React.FC<OverdueAgeingViewProps> = ({
  actions,
  stats,
  onSelect,
}) => {
  const getDaysOverdue = (dueDateStr: string) => {
    const due = new Date(dueDateStr);
    const now = new Date();
    due.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diff = now.getTime() - due.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  };

  const overdueActions = actions.filter((a) => {
    if (a.status === 'CLOSED' || a.status === 'CANCELLED') return false;
    return getDaysOverdue(a.currentDueDate) > 0;
  });

  const { sortedItems: sortedOverdue, sortField, sortDirection, toggleSort } = useTableSort(overdueActions);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* 4 Ageing Tier Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
        <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#b45309', fontWeight: 700, textTransform: 'uppercase' }}>
            Tier 1: 1–7 Days Overdue
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#d97706', margin: '6px 0 2px 0' }}>
            {stats?.ageingDistribution.days1to7 || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            Operational follow-up required
          </div>
        </div>

        <div style={{ background: '#fff7ed', border: '1px solid #ffedd5', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#c2410c', fontWeight: 700, textTransform: 'uppercase' }}>
            Tier 2: 8–30 Days Overdue
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#ea580c', margin: '6px 0 2px 0' }}>
            {stats?.ageingDistribution.days8to30 || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            Department manager escalation
          </div>
        </div>

        <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: '#b91c1c', fontWeight: 700, textTransform: 'uppercase' }}>
            Tier 3: 31–90 Days Overdue
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#dc2626', margin: '6px 0 2px 0' }}>
            {stats?.ageingDistribution.days31to90 || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            Safety Review Committee review
          </div>
        </div>

        <div style={{ background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: '8px', padding: '16px', color: '#ffffff' }}>
          <div style={{ fontSize: '11px', color: '#fca5a5', fontWeight: 700, textTransform: 'uppercase' }}>
            Tier 4: 90+ Days (Critical)
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#ffffff', margin: '6px 0 2px 0' }}>
            {stats?.ageingDistribution.days90Plus || 0}
          </div>
          <div style={{ fontSize: '11px', color: '#fca5a5' }}>
            Accountable Manager intervention
          </div>
        </div>
      </div>

      {/* Overdue Table */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
          Active Overdue Safety Actions ({overdueActions.length})
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
              <SortableTh field="actionNumber" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Action Ref
              </SortableTh>
              <SortableTh field="title" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Title
              </SortableTh>
              <SortableTh field="priority" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Priority
              </SortableTh>
              <SortableTh field="assignee" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Assignee
              </SortableTh>
              <SortableTh field="currentDueDate" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Target Due Date
              </SortableTh>
              <SortableTh field="currentDueDate" currentField={sortField} currentDirection={sortDirection} onSort={toggleSort} style={{ padding: '10px 16px', fontWeight: 600 }}>
                Ageing Delay
              </SortableTh>
              <th style={{ padding: '10px 16px', fontWeight: 600, textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedOverdue.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '36px', textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>
                  ✓ Outstanding! No safety actions currently overdue.
                </td>
              </tr>
            ) : (
              sortedOverdue.map((a) => {
                const days = getDaysOverdue(a.currentDueDate);
                return (
                  <tr
                    key={a.id}
                    onClick={() => onSelect(a)}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: '#dc2626' }}>
                      {a.actionNumber}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                      {a.title}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          background: a.priority === 'IMMEDIATE' ? '#dc2626' : '#ea580c',
                          color: '#ffffff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 700,
                        }}
                      >
                        {a.priority}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px' }}>
                      {a.assigneeName || 'Unassigned'} ({a.department || 'General'})
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: '#475569' }}>
                      {new Date(a.currentDueDate).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          background: days > 90 ? '#450a0a' : days > 30 ? '#fee2e2' : '#fef3c7',
                          color: days > 90 ? '#ffffff' : days > 30 ? '#b91c1c' : '#b45309',
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700,
                        }}
                      >
                        +{days} Days Overdue
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(a);
                        }}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #cbd5e1',
                          color: '#0f172a',
                          padding: '5px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Dossier ↗
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
